/**
 * Evidence list for a selected node or relationship — the traceability core
 * of the intelligence workflow. Every entry shows WHY the node/edge exists:
 * the supporting publication or procurement record, key context (year,
 * buyer/supplier/agency/amount), the source dataset, and a link back to the
 * underlying source record whenever one is available.
 */
import { FC } from 'react';
import { Box, Button, Chip, Divider, Stack, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { IntelEvidence, IntelProvenance } from '../../types/intelligenceGraph';

const DETAIL_LABELS: Record<string, string> = {
  year: 'Year',
  source: 'Source',
  publisher: 'Publisher',
  origin: 'Origin',
  buyer: 'Buyer',
  supplier: 'Supplier',
  agency: 'Agency',
  amount: 'Amount',
  tenderCode: 'Tender Code',
  createTime: 'Date',
  category: 'Category',
  area: 'Area',
};

function datasetLabel(dataset: string): string {
  return dataset === 'publications' ? 'Publications API' : 'PLA Contracts API';
}

function EvidenceItem({ item }: { item: IntelEvidence | IntelProvenance }) {
  const { detail } = item as IntelEvidence;
  return (
    <Box sx={{ py: 1 }}>
      <Typography variant="body2" fontWeight={600}>
        {item.evidence || item.sourceRecordId}
      </Typography>
      {detail && (
        <Stack spacing={0.25} sx={{ mt: 0.5 }}>
          {Object.entries(detail)
            .filter(([, value]) => Boolean(value))
            .map(([key, value]) => (
              <Typography key={key} variant="caption" color="text.secondary">
                {DETAIL_LABELS[key] ?? key}: {value}
              </Typography>
            ))}
        </Stack>
      )}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mt: 0.75 }}
        flexWrap="wrap"
      >
        <Chip
          size="small"
          variant="outlined"
          label={datasetLabel(item.sourceDataset)}
        />
        {item.url && (
          <Button
            size="small"
            variant="outlined"
            endIcon={<Icon icon="mdi:open-in-new" width={14} />}
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {item.sourceDataset === 'publications'
              ? 'Open Publication'
              : 'Open Procurement Record'}
          </Button>
        )}
      </Stack>
    </Box>
  );
}

export const EvidencePanel: FC<{
  title?: string;
  evidence: (IntelEvidence | IntelProvenance)[];
}> = ({ title = 'Evidence', evidence }) => (
  <Box>
    <Typography variant="overline" color="text.secondary">
      {title} ({evidence.length})
    </Typography>
    {evidence.length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        No evidence records captured.
      </Typography>
    ) : (
      <Stack divider={<Divider flexItem />}>
        {evidence.map((item, index) => (
          <EvidenceItem
            key={`${item.sourceDataset}:${item.sourceRecordId}:${index}`}
            item={item}
          />
        ))}
      </Stack>
    )}
  </Box>
);
